import { useMemo } from 'react';
import { useSubscription } from '@hooks/api/subscription/useSubscription';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { OrganizationSubscriptionStatus } from '@models/subscription';
import { UserRoles } from '@models/user.model';

/** Show the countdown from here on. Matches the T-7 reminder email. */
export const TRIAL_WARNING_THRESHOLD_DAYS = 7;

/** Switch the countdown to its most urgent styling. Matches the T-1 reminder email. */
export const TRIAL_URGENT_THRESHOLD_DAYS = 1;

export interface TrialStatus {
  isLoading: boolean;
  /** On a live trial that has not lapsed. */
  isOnTrial: boolean;
  /** Trial has lapsed — the organization is read-only until a card is added. */
  isTrialExpired: boolean;
  /**
   * Whether the lapsed trial actually restricts *this* user. Always false for a SUPER_ADMIN:
   * they operate the platform rather than rent it, and every mutation that lifts the paywall
   * is OWNER-only, so gating them leaves them with no way out. Gate on this, not on
   * `isTrialExpired` — that one stays a plain fact about the organization.
   */
  isBillingLocked: boolean;
  /** Whole days until the trial ends. Null when not on a trial. */
  daysRemaining: number | null;
  /** True inside the final week, i.e. when the countdown is worth showing. */
  isEndingSoon: boolean;
}

/**
 * Derives everything the trial UI needs from the organization's BASE subscription.
 *
 * Single source of truth so the sidebar pill, the dashboard banner and the write-gating
 * hook can never disagree about whether a trial has lapsed.
 */
export const useTrialStatus = (): TrialStatus => {
  const { data: subscription, isLoading } = useSubscription();
  const role = useAppSelector((state: RootState) => state.user.role);
  const isPlatformAdmin = role === UserRoles.SUPER_ADMIN;

  return useMemo(() => {
    if (!subscription) {
      return {
        isLoading,
        isOnTrial: false,
        isTrialExpired: false,
        isBillingLocked: false,
        daysRemaining: null,
        isEndingSoon: false,
      };
    }

    const isTrialExpired =
      subscription.status === OrganizationSubscriptionStatus.TRIAL_EXPIRED;
    const isOnTrial =
      subscription.status === OrganizationSubscriptionStatus.TRIAL;

    let daysRemaining: number | null = null;

    if (isOnTrial && subscription.trialEndsAt) {
      const msPerDay = 24 * 60 * 60 * 1000;
      // Ceil so a partial day still reads as a day left — telling someone "0 days"
      // while they can still write would be wrong.
      daysRemaining = Math.max(
        0,
        Math.ceil(
          (new Date(subscription.trialEndsAt).getTime() - Date.now()) / msPerDay,
        ),
      );
    }

    return {
      isLoading,
      isOnTrial,
      isTrialExpired,
      isBillingLocked: isTrialExpired && !isPlatformAdmin,
      daysRemaining,
      isEndingSoon:
        daysRemaining !== null &&
        daysRemaining <= TRIAL_WARNING_THRESHOLD_DAYS,
    };
  }, [subscription, isLoading, isPlatformAdmin]);
};

export default useTrialStatus;
